import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useReport } from '../../hooks/useReport';
import { useReportUrlFilters } from '../../hooks/useReportUrlFilters';
import ReportFiltersConfiguration from '../ReportFiltersConfiguration/ReportFiltersConfiguration';

function ReportConfigurePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { report, loading } = useReport(id);
  const [selectedFilters, setSelectedFilters] = useState(null);

  // Parse filters from URL query parameters (for external linking with pre-filled values)
  const queryParamFilters = useReportUrlFilters();

  // Check if we're editing an existing saved report
  const savedReportState = useMemo(() => location.state || {}, [location.state]);
  const {
    isEditMode = false,
    savedReportId,
    savedReportName,
    savedReportDescription,
    initialFilters: stateInitialFilters,
  } = savedReportState;

  // Priority: location.state filters > URL query param filters
  const initialFilters = stateInitialFilters || queryParamFilters;

  const handleGenerate = useCallback(
    (filtersData) => {
      setSelectedFilters(filtersData);
      navigate(`/${id}/view`, {
        state: {
          filters: filtersData,
          // Pass edit mode state to the viewer
          isEditMode,
          savedReportId,
          savedReportName,
          savedReportDescription,
          // Pass the original saved report configuration so "Back" can restore it
          savedReportConfiguration: stateInitialFilters,
        },
      });
    },
    [id, navigate, isEditMode, savedReportId, savedReportName, savedReportDescription, stateInitialFilters],
  );

  const handleCancel = useCallback(() => {
    // If editing a saved report, go back to saved reports page
    if (isEditMode) {
      navigate('/saved');
    } else {
      navigate('/');
    }
  }, [navigate, isEditMode]);

  // Show loading while report is being fetched
  if (loading || !report) {
    return null; // Suspense fallback will show
  }

  return (
    <ReportFiltersConfiguration
      report={report}
      onGenerate={handleGenerate}
      onCancel={handleCancel}
      initialFilters={initialFilters || selectedFilters}
      isEditMode={isEditMode}
      savedReportName={savedReportName}
    />
  );
}

export default ReportConfigurePage;
